// src/components/pages/post/WritePost.js

import React, { useRef, useState, useEffect, useMemo, useCallback } from 'react';
import ReactDOM from 'react-dom';
import ReactQuill from 'react-quill';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import 'react-quill/dist/quill.snow.css';
import styles from './style/WritePost.module.css';
import { v4 as uuidv4 } from 'uuid';
import { useTranslation } from 'react-i18next';
import TagInput from './TagInput';
import Quill from 'quill';
import QuillEditor from './partials/QuillEditor';
import ImageResize from 'quill-image-resize-module-react';
import imageCompression from 'browser-image-compression';

const API_URL = process.env.REACT_APP_API_BASE_URL;

Quill.register('modules/imageResize', ImageResize);

function ConfirmationModal({ isOpen, onCancel, onConfirm, children, auth, loading, uploadingPreviewImage }) {
  const { t } = useTranslation();
  if (!isOpen) return null;
  return ReactDOM.createPortal(
    <div className={styles.modalOverlay}>
      <div className={styles.modalContent}>
        {children}
        <div className={styles.modalActions}>
          <button onClick={onCancel} className={styles.cancelBtn}>{t('writePost.cancel')}</button>
          <button
            onClick={onConfirm}
            className={styles.confirmBtn}
            disabled={auth.isLoading || !auth.isAuthenticated || loading || uploadingPreviewImage}
            title={!auth.isAuthenticated ? t('writePost.loginRequiredTooltip') : ''}
          >
            {t('writePost.confirm')}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

const TEMP_SAVE_KEY = 'temp_post_content';

export default function WritePostPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { postId } = useParams();
  const auth = useAuth();

  const userId = auth.user?.profile?.sub;
  const idToken = auth.user?.id_token;
  const quillRef = useRef(null);

  const [title, setTitle] = useState('');
  const [previewTitle, setPreviewTitle] = useState('');
  const [tags, setTags] = useState([]);
  const [content, setContent] = useState('');
  const [pendingImages, setPendingImages] = useState([]);
  const [isPaid, setIsPaid] = useState(false);
  const [isPrivate, setIsPrivate] = useState(false);
  const [loading, setLoading] = useState(false);
  const [uploadingPreviewImage, setUploadingPreviewImage] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const [previewImageUrl, setPreviewImageUrl] = useState('');
  const [existingImageKeys, setExistingImageKeys] = useState([]);

  
  const [quillKey] = useState(uuidv4());
  const isEditMode = !!postId;


  // useEffect(() => {
  //   if (!auth.isLoading && !auth.isAuthenticated) {
  //     sessionStorage.setItem('redirect_after_login', window.location.pathname);
  //     auth.signinRedirect();
  //   }
  // }, [auth, navigate]);

  



  useEffect(() => {
    if (!isEditMode) {
      const savedData = localStorage.getItem(TEMP_SAVE_KEY);
      if (savedData) {
        try {
          const { title, content, previewTitle, tags, isPaid, isPrivate } = JSON.parse(savedData);
          setTitle(title || '');
          setContent(content || '');
          setPreviewTitle(previewTitle || '');
          setTags(tags || []);
          setIsPaid(isPaid || false);
          setIsPrivate(isPrivate || false);
        } catch (e) {
          console.error('Failed to parse saved data from localStorage', e);
          localStorage.removeItem(TEMP_SAVE_KEY);
        }
      }
    }
  }, [isEditMode]);

  useEffect(() => {
    if (!isEditMode) {
      const timer = setTimeout(() => {
        const dataToSave = JSON.stringify({
          title,
          content,
          previewTitle,
          tags,
          isPaid,
          isPrivate,
        });
        localStorage.setItem(TEMP_SAVE_KEY, dataToSave);
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [title, content, previewTitle, tags, isPaid, isPrivate, isEditMode]);

  useEffect(() => {
    if (isEditMode && idToken) {
      setLoading(true);
      fetch(`${API_URL}/posts/${postId}`, {
        headers: {
          Authorization: `Bearer ${idToken}`,
        },
      })
        .then(res => {
          if (!res.ok) throw new Error(`Status ${res.status}: ${res.statusText}`);
          return res.json();
        })
        .then(data => {
          setTitle(data.title || '');
          setPreviewTitle(data.previewTitle || '');
          setContent(data.content || '');
          setTags(data.tags || []);
          setIsPaid(data.isPaid || false);
          setIsPrivate(data.isPrivate || false);
          setPreviewImageUrl(data.previewImage ? `${process.env.REACT_APP_CDN_URL}/${data.previewImage}` : '');
          setExistingImageKeys(data.imageKeys || []);
        })
        .catch(err => {
          console.error('Fetch post error:', err);
          alert(t('writePost.fetchError', { message: err.message }));
        })
        .finally(() => setLoading(false));
    }
  }, [isEditMode, postId, idToken, t]);

  const handleLocalImage = useCallback(file => {
    const MAX_SIZE_MB = 2;
    const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024;
    if (file.size > MAX_SIZE_BYTES) {
      alert(t('writePost.imageSizeError', { max_size: MAX_SIZE_MB }));
      return;
    }
    const id = uuidv4();
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result;
      const quill = quillRef.current.getEditor();
      const range = quill.getSelection(true);
      quill.insertEmbed(range.index, 'image', dataUrl);
      quill.setSelection(range.index + 1);
      setPendingImages(prev => [...prev, { id, file, dataUrl }]);
    };
    reader.readAsDataURL(file);
  }, [t]);

  const imageHandler = useCallback(() => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.click();
    input.onchange = () => input.files?.[0] && handleLocalImage(input.files[0]);
  }, [handleLocalImage]);

  const handlePaste = useCallback(e => {
    const clipboard = e.clipboardData;
    if (!clipboard) return;
    for (const item of clipboard.items) {
      if (item.type.startsWith('image/')) {
        e.preventDefault();
        const file = item.getAsFile();
        file && handleLocalImage(file);
      }
    }
  }, [handleLocalImage]);

  const uploadToS3 = async (file, idToken) => {
    try {
      console.log('Uploading file:', file.name, file.type);
      const presignRes = await fetch(`${API_URL}/posts/upload-url`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
        },
        body: JSON.stringify({ filename: file.name, fileType: file.type }),
        mode: 'cors',
      });
      if (!presignRes.ok) {
        throw new Error(`Failed to get presigned URL: ${presignRes.status} ${presignRes.statusText}`);
      }
      const { uploadUrl, key } = await presignRes.json();
      console.log('Presigned URL:', uploadUrl);
      const response = await fetch(uploadUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': file.type,
        },
        body: file,
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`S3 upload failed: ${response.status} ${response.statusText} - ${errorText}`);
      }
      return { url: `${process.env.REACT_APP_CDN_URL}/${key}`, key };
    } catch (err) {
      console.error('S3 upload error:', err);
      throw err;
    }
  };

  const handlePreviewImage = useCallback(async () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.click();
    input.onchange = async () => {
      if (input.files?.[0]) {
        const file = input.files[0];
        setUploadingPreviewImage(true);

        try {
          const options = {
            maxSizeMB: 0.5,
            maxWidthOrHeight: 600,
            useWebWorker: true,
            fileType: 'image/webp',
          };
          const compressedFile = await imageCompression(file, options);
          console.log(`Original: ${file.size / 1024} KB, Compressed: ${compressedFile.size / 1024} KB`);
          const { url } = await uploadToS3(compressedFile, idToken);
          setPreviewImageUrl(url);
          setPreviewFile(compressedFile);
        } catch (err) {
          console.error('Preview image upload error:', err);
          alert(t('writePost.previewUploadError', { message: err.message }));
        } finally {
          setUploadingPreviewImage(false);
        }
      }
    };
  }, [idToken, t, uploadToS3]);

  const modules = useMemo(() => ({
    toolbar: {
      container: [
        [{ size: ['small', false, 'large', 'huge'] }],
        [{ header: [1, 2, 3, 4, 5, 6] }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ color: [] }, { background: [] }],
        [{ script: 'sub' }, { script: 'super' }],
        ['blockquote', 'code-block'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        [{ indent: '-1' }, { indent: '+1' }],
        [{ direction: 'rtl' }],
        [{ align: [] }],
        ['link', 'image', 'video'],
        ['clean'],
      ],
      handlers: { image: imageHandler },
    },
    imageResize: {
      parchment: Quill.import('parchment'),
      modules: ['Resize', 'DisplaySize'],
    },
    clipboard: {
      // matchVisual: true를 설정하면 HTML을 붙여넣을 때 보이는 스타일을 최대한 유지합니다.
      matchVisual: true,
    },

  }), [imageHandler]);

  const formats = [
    'size', 'header', 'bold', 'italic', 'underline', 'strike',
    'color', 'background', 'script', 'blockquote', 'code-block',
    'list', 'bullet', 'ordered', 'indent', 'direction', 'align',
    'link', 'image', 'video', 'clean',
  ];

  useEffect(() => {
    const editor = quillRef.current?.getEditor();
    if (!editor) return;
    editor.root.addEventListener('paste', handlePaste);
    return () => editor.root.removeEventListener('paste', handlePaste);
  }, [handlePaste]);

  const submitPost = useCallback(async () => {
    if (!auth.isAuthenticated || !idToken) {
      alert(t('writePost.loginRequired'));
      return auth.signinRedirect();
    }
    setLoading(true);
    try {
      let html = quillRef.current.getEditor().root.innerHTML;
      const toUpload = pendingImages.filter(pi => html.includes(pi.dataUrl));
      const uploadedKeys = [...existingImageKeys];
      for (const { file, dataUrl } of toUpload) {
        const { url, key } = await uploadToS3(file, idToken);
        html = html.split(dataUrl).join(url);
        uploadedKeys.push(key);
      }
      
      let previewImageKey = '';
      if (previewImageUrl) {
        const urlObj = new URL(previewImageUrl);
        previewImageKey = urlObj.pathname.substring(1);
      }

      const payload = {
        title,
        previewTitle,
        content: html,
        tags: tags.map(t => t.trim()).filter(Boolean),
        imageKeys: uploadedKeys,
        isPaid,
        isPrivate,
        previewImage: previewImageKey,
      };

      const url = isEditMode ? `${API_URL}/posts/${postId}` : `${API_URL}/posts`;
      const method = isEditMode ? 'PUT' : 'POST';
      const res = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
        },
        body: JSON.stringify(payload),
        mode: 'cors',
      });

      console.log('payload', payload)
      if (!res.ok) {
        throw new Error(`${isEditMode ? 'Update' : 'Post'} failed: ${res.status} ${res.statusText}`);
      }
      const data = isEditMode ? {} : await res.json();
      
      localStorage.removeItem(TEMP_SAVE_KEY);

      navigate(isEditMode ? `/posts/${postId}` : `/posts/${data.id}`);
    } catch (err) {
      console.error(`${isEditMode ? 'Update' : 'Submit'} error:`, err);
      alert(t(isEditMode ? 'writePost.updateFailed' : 'writePost.submitFailed', { message: err.message }));
    } finally {
      setLoading(false);
    }
  }, [auth, idToken, pendingImages, title, previewTitle, tags, isPaid, isPrivate, postId, previewFile, previewImageUrl, existingImageKeys, isEditMode, navigate, uploadingPreviewImage, t]);
  
  const handleFormSubmit = e => {
    e.preventDefault();
    // 제목이 비어 있거나 공백만 있는지 확인
    if (!title.trim()) {
      alert(t('writePost.titleRequired'));
      return;
    }

    // 내용이 비어 있는지 확인
    // Quill 에디터의 content는 HTML 문자열이므로,
    // HTML 태그와 공백을 제거하고 순수 텍스트가 비어 있는지 확인합니다.
    const plainTextContent = content.replace(/<[^>]+>/g, '').trim();
    if (!plainTextContent) {
      alert(t('writePost.contentRequired'));
      return;
    }

    setShowModal(true);
  };

  return (
    <div className={styles.wrapper}>
      <form onSubmit={handleFormSubmit} className={styles.form}>
        <div className={styles.fieldGroup}>
          <input
            type="text"
            value={title}
            onChange={e => setTitle(e.target.value)}
            className={styles.titleInput}
            placeholder={t('writePost.titlePlaceholder')}
          />
        </div>
        <div className={styles.editorWrapper}>
          <QuillEditor
                key={`${quillKey}-${i18n.language}`}
                ref={quillRef}
                content={content}
                setContent={setContent}
                handlePaste={handlePaste}
                imageHandler={imageHandler}
                styles={styles}
                t={t} // t 함수를 props로 전달
          />
        </div>
        <button
          type="submit"
          disabled={loading || auth.isLoading || !auth.isAuthenticated}
          className={styles.submitBtn}
          title={!auth.isAuthenticated ? t('writePost.loginRequiredTooltip') : ''}
        >
          {auth.isLoading
            ? t('writePost.authChecking')
            : (loading ? t('writePost.processing') : isEditMode ? t('writePost.editPost') : t('writePost.submitPost'))}
        </button>
      </form>

      <ConfirmationModal
        isOpen={showModal}
        onCancel={() => setShowModal(false)}
        onConfirm={() => { setShowModal(false); submitPost(); }}
        auth={auth}
        loading={loading}
        uploadingPreviewImage={uploadingPreviewImage}
      >
        <h2>{isEditMode ? t('writePost.modalEditTitle') : t('writePost.modalSettingsTitle')}</h2>
        <div className={styles.fieldGroup}>
          <input
            type="text"
            value={previewTitle}
            onChange={e => setPreviewTitle(e.target.value)}
            className={styles.previewInput}
            placeholder={t('writePost.previewPlaceholder')}
          />
        </div>
        <div className={styles.fieldGroup}>
          <TagInput
            tags={tags}
            setTags={setTags}
            placeholder={t('writePost.tagsPlaceholder')}
          />
        </div>
        <div className={styles.fieldGroup}>
          <button
            type="button"
            onClick={handlePreviewImage}
            disabled={uploadingPreviewImage || loading || auth.isLoading || !auth.isAuthenticated}
            className={styles.previewImageBtn}
            title={!auth.isAuthenticated ? t('writePost.loginRequiredTooltip') : ''}
          >
            {uploadingPreviewImage ? t('writePost.uploading') : t('writePost.uploadPreviewImage')}
          </button>
          {previewImageUrl && (
            <div className={styles.previewImageContainer}>
              <img src={previewImageUrl} alt="Preview" className={styles.previewImage} />
              <button
                type="button"
                onClick={() => {
                  setPreviewImageUrl('');
                  setPreviewFile(null);
                }}
                className={styles.removePreviewBtn}
              >
                {t('writePost.remove')}
              </button>
            </div>
          )}
        </div>

        <div className={styles.options}>
          {/* <label className={styles.toggleLabel}>
            <input
              type="checkbox"
              checked={isPaid}
              onChange={e => setIsPaid(e.target.checked)}
              disabled={true}
            />
            <span className={styles.toggleText}>{t('writePost.paidPost')}</span>
          </label> */}
          <p className={styles.comingSoonText}>
            {/* {t('writePost.comingSoon')} */}
            {t('writePost.comingSoon')}
          </p>
        </div>
      </ConfirmationModal>
    </div>
  );
}